/* ============================================================
   TabletRail.js -- persistent navigation for iPad
   ------------------------------------------------------------
   FOX has three navigations now, and this is the one for the
   middle: a screen wide enough to hold the desktop layout but
   driven by a finger.

     phone   (< 768px)          bottom tab bar + sheet
     tablet  (>= 768px, touch)  this rail
     desktop (mouse)            the header menu, unchanged

   The header menu was never a tablet control either. It opens
   on hover, which an iPad does not have, and even with the tap
   handling added earlier it hides 29 dashboards behind eight
   dropdowns that each need a tap to reveal and a tap to dismiss.
   A rail shows the whole structure at once and costs one tap to
   use, which is the entire point of having 834 points of width.

   ------------------------------------------------------------
   WHY THE MEDIA QUERY IS SHAPED LIKE THAT

   `(min-width: 768px) and (any-pointer: coarse)` -- not a width
   alone, and not `pointer: coarse` alone.

   Width alone would put the rail on a 1280px laptop window,
   which is a desktop and should keep its header menu.

   `pointer: coarse` alone reports FINE on an iPad with a Magic
   Keyboard attached, because the trackpad becomes the primary
   pointer. That is why the touch sizing shipped earlier was
   invisible on exactly those iPads. `any-pointer` asks whether
   ANY available pointer is coarse, and an iPad has a touchscreen
   whatever else is plugged into it.

   The one deliberate consequence: a touchscreen Windows laptop
   gets the rail. It is a touch device, so that is defensible,
   and the query is a single string in viewport.js if it turns
   out not to be.
   ============================================================ */
import React from "react";
import "./TabletRail.css";
import { useHeaderOffset } from "./viewport";

export default function TabletRail({
  sectionEntries,
  currentPage,
  setCurrentPage,
  pageLabel,
  pageIcons,
  alertCount = 0,
  onPrefetch,
  currentUser,
}) {
  const headerOffset = useHeaderOffset();
  const initials = (currentUser?.full_name || "")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <nav
      className="trail"
      aria-label="Dashboards"
      style={{ top: headerOffset, height: `calc(100dvh - ${headerOffset}px)` }}
    >
      <div className="trail-scroll">
        {sectionEntries.map(([sectionName, pages]) => (
          <div className="trail-group" key={sectionName}>
            <span className="trail-group-label">{sectionName}</span>
            {pages.map((page) => {
              const PageIcon = pageIcons[page];
              const active = page === currentPage;
              return (
                <button
                  type="button"
                  key={page}
                  className={`trail-item${active ? " active" : ""}`}
                  aria-current={active ? "page" : undefined}
                  // The rail is always on screen, so a finger landing on a row
                  // is the earliest warning the chunk is about to be needed.
                  onPointerDown={() => onPrefetch?.(page)}
                  onClick={() => setCurrentPage(page)}
                >
                  <span className="trail-item-icon">
                    {PageIcon ? <PageIcon size={17} strokeWidth={1.85} /> : null}
                  </span>
                  <span className="trail-item-label">{pageLabel(page)}</span>
                  {page === "Alerts" && alertCount > 0 && (
                    <span className="trail-item-count">{alertCount}</span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {currentUser?.full_name && (
        <div className="trail-foot">
          <span className="trail-foot-avatar">{initials}</span>
          <span className="trail-foot-name">{currentUser.full_name}</span>
        </div>
      )}
    </nav>
  );
}
